import * as vp from 'vopay';
import * as Yup from 'yup';
import { format, addDays, isBefore, startOfDay, isEqual, isAfter } from 'date-fns';
import React, { JSX, useState } from 'react';
import { FieldData, FormCtx } from '../../Form/types';
import { ElinxInfo, Contact, ElinxType } from './types';
import styles from './ElinxPages.module.scss';
import SelectOptionLocked from '@/components/UI/Select/SelectOptionLocked';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useTranslation } from 'react-i18next';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

const classes = vp.withStyles(styles);

const {
  Chip,
  CardCheckbox,
  Tooltip,
  Box,
  Button,
  Icon,
  Select,
  TextField,
  GoogleAddressField,
  MultiSelect,
  DatepickerSimple,
  Radio,
  InteracWordmark,
  UploadFile,
} = vp;

export function useElinxBasicDetailsRows(
  contacts: Contact[],
  onContactSearch: (val: string) => void,
  showMore: boolean,
  formType: ElinxType,
  country: string,
  usdEftEnabled: boolean,
): FieldData<ElinxInfo>[] {
  const [isNewContact, setIsNewContact] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.BasicDetails' });
  const FrequencyOptions = Object.entries(vp.FREQUENCY_OPTIONS).map(([key]) => ({
    label: t(`Frequency.Options.${key}`),
    value: key,
  }));

  const CurrencyOptions: vp.SelectOption<BasicOptions>[] = [
    { label: 'CAD', value: 'CAD', isVisible: country === 'CA' },
    { label: 'USD', value: 'USD', isVisible: country !== 'CA' || (country === 'CA' && usdEftEnabled) },
  ];
  const ScheduleTypeOptions: vp.SelectOption<BasicOptions>[] = [
    { label: t('ScheduleType.Options.single'), value: 'single' },
    { label: t('ScheduleType.Options.recurring'), value: 'recurring' },
  ];

  const EndingOptions: vp.SelectOption<BasicOptions>[] = [
    { label: t('Ending.Options.endDate'), value: 'End Date' },
    { label: t('Ending.Options.payments'), value: 'Payments' },
  ];

  const SemiMonthlyDateOptions: vp.SelectOption<BasicOptions>[] = [
    { label: t('SemiMonthlyFrequencyType.Options.semiMonthFirstAndFifteenth'), value: 'SemiMonthFirstAndFifteenth' },
    { label: t('SemiMonthlyFrequencyType.Options.semiMonthFifteenthAndLast'), value: 'SemiMonthFifteenthAndLast' },
  ];

  const now = format(new Date(), 'yyyy-MM-dd');

  return [
    {
      label: () => t('Contact.label'),
      fieldName: 'Contact',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) => (values.Contact === 'new' ? undefined : values.Contact),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            filterable
            onFilter={(e) => {
              e.preventDefault();
              e.debounce(() => {
                onContactSearch(e.filter);
              }, 1000);
            }}
            options={contacts
              .filter(
                (contact) =>
                  !!contact.name && contact.name.split(' ').filter((contactFilter) => !!contactFilter).length > 1,
              )
              .slice(0, 99)}
            optionValue={(o) => o.id}
            renderSelectedValue={(data, { Placeholder }) => {
              if (isNewContact) return 'New Contact';

              if (!data) return <Placeholder />;

              return data.name;
            }}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault, disabled }) => (
              <Option
                onClick={() => {
                  if (disabled) {
                    preventDefault();
                  } else {
                    setIsNewContact(false);
                    ctx.setFieldValue('OverrideEmailAddress', o.email);
                    ctx.setFieldValue('ExistingContactName', o.name);
                  }
                }}
              >
                {o.name}
                {selected && <SelectedIcon />}
              </Option>
            )}
            renderDropdownFooter={({ close }) => (
              <Box flx="col">
                <Button
                  id="addNewContact"
                  className={styles.greyButton}
                  label={
                    <Box flx="row" gapSize="8px" jc="center" ai="center">
                      <Icon height="20px" width="20px" icon="add" color="medium-dark" />
                      <span>{t('Contact.dropdownButton')}</span>
                    </Box>
                  }
                  onClick={() => {
                    setIsNewContact(true);
                    ctx.setFieldValue('Contact', 'new');
                    close();
                  }}
                />
              </Box>
            )}
            placeholder={t('Contact.placeholder')}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('Currency.label'),
      fieldName: 'Currency',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => values.Currency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('Currency.placeholder')}
            options={CurrencyOptions.filter((option) => option.isVisible)}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('Amount.label'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => vp.sanitizeToNumeric(values.Amount),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField variant="outlineShadow" preset="currency" placeholder={t('Amount.placeholder')} {...fieldData} />
        );
      },

      validate: () =>
        Yup.string()
          .test('amount-validation', t('Amount.Validation.greaterThanZero'), (value) => {
            const amountValidation = Number(vp.sanitizeToNumeric(value)) <= 0.0;
            return !amountValidation;
          })
          .required(),
    },
    {
      label: () => t('ScheduleType.label'),
      fieldName: 'Frequency',
      getInputState: () => (formType === 'Flex' ? 'disabled' : 'required'),
      showField: () => formType !== 'Connect' && formType !== 'Flex',
      serializeField: (values: ElinxInfo) => {
        if (isToday(values.ScheduleStartDate) && values.Frequency === 'single') {
          return undefined;
        }

        return values.Frequency;
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (
              e.target.value === 'recurring' &&
              ctx.values.ScheduleStartDate &&
              isToday(ctx.values.ScheduleStartDate)
            ) {
              const selectedDate = new Date(Number(ctx.values.ScheduleStartDate));
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleStartDate', newDate.toString());
            }
          },
        });

        return (
          <Select
            version="2"
            placeholder={t('ScheduleType.placeholder')}
            options={ScheduleTypeOptions}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required(),
    },
    {
      label: (ctx: FormCtx<ElinxInfo>) =>
        ctx.values.Frequency === 'recurring'
          ? t('ScheduleStartDate.Label.recurring')
          : formType === 'Pay' || formType === 'Flex'
            ? t('ScheduleStartDate.Label.requestedTransaction')
            : t('ScheduleStartDate.Label.collection'),
      fieldName: 'ScheduleStartDate',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect' && formType !== 'Flex',
      serializeField: (values: ElinxInfo) => {
        const startDate = new Date(Number(values.ScheduleStartDate));
        if (isToday(values.ScheduleStartDate) && values.Frequency === 'single') {
          return undefined;
        }

        return format(startDate, 'yyyy-MM-dd');
      },

      tooltip: (ctx) =>
        ctx.values.Frequency !== 'recurring' ? (
          <Tooltip information={t('ScheduleStartDate.tooltip')} position="top" width="394px" />
        ) : undefined,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const selectedDate = new Date(Number(e.target.value));
            const endDate = new Date(Number(ctx.values.ScheduleEndDate));
            if (
              isAfter(selectedDate, endDate) ||
              format(selectedDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
            ) {
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleEndDate', newDate.toString());
            }
          },
        });
        return (
          <DatepickerSimple
            variant="outlineShadow"
            isDisabled={fieldData.disabled}
            min={new Date().getTime() as vp.Timestamp}
            iconPosition="left"
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.string()
          .required()
          .test(
            'start-date-validation',
            t('ScheduleStartDate.Validation.invalidDate', { now }),
            (value: string | undefined) => {
              const dateValue = value ? new Date(Number(value)) : undefined;
              return !dateValue || format(dateValue, 'yyyy-MM-dd') >= now;
            },
          ),
    },
    {
      label: () => t('Frequency.label'),
      fieldName: 'NameOfFrequency',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => values.NameOfFrequency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select version="2" placeholder={t('Frequency.placeholder')} options={FrequencyOptions} {...fieldData} />
        );
      },

      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('SemiMonthlyFrequencyType.label'),
      fieldName: 'SemiMonthlyFrequencyType',
      showField: (values: ElinxInfo) => values.NameOfFrequency === 'semi-monthly',
      getInputState: () => 'required',
      serializeField: (values: ElinxInfo) => values.SemiMonthlyFrequencyType,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('SemiMonthlyFrequencyType.placeholder')}
            options={SemiMonthlyDateOptions}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('Ending.label'),
      fieldName: 'Ending',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => values.Ending,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" placeholder={t('Ending.placeholder')} options={EndingOptions} {...fieldData} />;
      },
      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('EndingAfterPayments.label'),
      fieldName: 'EndingAfterPayments',
      getInputState: () => 'required',

      showField: (values: ElinxInfo) => values.Ending === 'Payments' && values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => vp.sanitizeToNumeric(values.EndingAfterPayments),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="numericWholePositive"
            placeholder={t('EndingAfterPayments.placeholder')}
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.number()
          .transform((value) => Number(value) || 0)
          .min(1, t('EndingAfterPayments.Validation.atLeastOne'))
          .required(),
    },
    {
      label: () => t('ScheduleEndDate.label'),
      fieldName: 'ScheduleEndDate',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Ending === 'End Date' && values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => {
        const endDate = new Date(Number(values.ScheduleEndDate));
        return format(endDate, 'yyyy-MM-dd');
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <DatepickerSimple
            variant="outlineShadow"
            min={addDays(new Date(Number(ctx.values.ScheduleStartDate)), 1).getTime() as vp.Timestamp}
            iconPosition="left"
            placeholder=""
            {...fieldData}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: (ctx: FormCtx<ElinxInfo>) =>
        Yup.string()
          .required()
          .test('end-date-validation', t('ScheduleEndDate.Validation.invalidDate'), (value: string | undefined) => {
            const endDate = value ? new Date(Number(value)) : undefined;
            const startDate = new Date(Number(ctx.values.ScheduleStartDate));
            if (!endDate) return false;
            return !isEqual(endDate, startDate) && !isBefore(endDate, startDate);
          }),
    },
    {
      label: () => t('EmailAddress.label'),
      fieldName: 'OverrideEmailAddress',
      getInputState: () => 'required',
      showField: (values) => values.Contact !== 'new',
      serializeField: (values: ElinxInfo) => values.OverrideEmailAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder={t('EmailAddress.placeholder')} {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.Contact !== 'new' ? Yup.mixed().required() : Yup.mixed()),
    },
    {
      label: () => t('TransactionLabel.label'),
      fieldName: 'TransactionLabel',
      getInputState: () => 'optional',
      showField: () => showMore,
      serializeField: (values: ElinxInfo) => values.TransactionLabel,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
    {
      label: () => t('Note.label'),
      fieldName: 'Note',
      getInputState: (ctx) => (ctx.values.Frequency === 'recurring' ? 'required' : 'optional'),
      showField: (values: ElinxInfo) => (values.Frequency === 'recurring' ? true : showMore),
      serializeField: (values: ElinxInfo) => values.Note,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.Frequency === 'recurring' ? Yup.mixed().required() : Yup.mixed()),
    },
  ];
}

export function useElinxRowsContactDetails(): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactDetails' });

  return [
    {
      label: () => t('FirstName.label'),
      fieldName: 'FirstName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.FirstName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('FirstName.placeholder')} {...fieldData} />;
      },

      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('LastName.label'),
      fieldName: 'LastName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.LastName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('LastName.placeholder')} {...fieldData} />;
      },

      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('EmailAddress.label'),
      fieldName: 'NewEmailAddress',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.NewEmailAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="email"
            placeholder={t('EmailAddress.placeholder')}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required(),
    },
  ];
}

export function useElinxContactAddressRows(Country: string): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactAddress' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Countries' });

  const CountryOptions = [
    { label: tGlobal('CA'), value: 'CA' },
    { label: tGlobal('USLong'), value: 'US' },
  ].filter((o) => o.value === Country);

  function getRequiredInputState() {
    return Country === 'CA' ? 'optional' : 'required';
  }

  return [
    {
      label: () => t('Address.label'),
      fieldName: 'AddressLine1',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.AddressLine1,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <GoogleAddressField
            variant="outlineShadow"
            ISOCountry={[Country]}
            onGoogleAddressSelect={(addressComponents) => {
              ctx.setFieldValue('AddressLine1', addressComponents.address);
              ctx.setFieldValue('City', addressComponents.city);
              ctx.setFieldValue('Province', addressComponents.province);
              ctx.setFieldValue('PostalCode', addressComponents.postalcode);
              ctx.setFieldValue('Country', addressComponents.country);
              ctx.setFieldTouched('AddressLine1', true);
              ctx.setFieldTouched('City', true);
              ctx.setFieldTouched('Province', true);
              ctx.setFieldTouched('PostalCode', true);
              ctx.setFieldTouched('Country', true);
            }}
            placeholder={t('Address.placeholder')}
            {...fieldData}
          />
        );
      },
      validate: () => (Country === 'CA' ? Yup.mixed() : Yup.mixed().required()),
    },
    {
      label: () => t('Unit.label'),
      fieldName: 'AddressLine2',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.AddressLine2,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('Unit.placeholder')} {...fieldData} />;
      },
    },
    {
      label: () => t('City.label'),
      fieldName: 'City',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.City,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('City.placeholder')} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.mixed() : Yup.mixed().required()),
    },
    {
      label: () => t('Province.label'),
      fieldName: 'Province',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.Province,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('Province.placeholder')} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.mixed() : Yup.mixed().required()),
    },
    {
      label: () => t('Country.label'),
      fieldName: 'Country',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.Country,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" placeholder={t('Country.placeholder')} options={CountryOptions} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.mixed() : Yup.mixed().required()),
    },
    {
      label: () => t('PostalCode.label'),
      fieldName: 'PostalCode',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.PostalCode,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('PostalCode.placeholder')} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.mixed() : Yup.mixed().required()),
    },
  ];
}

export function useElinxPaymentSettingsRows(
  formType: ElinxType,
  country: string,
  contactName: string,
  paymentMethods: vp.PaymentMethod[],
): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactPaymentSettings' });

  const AccountSelectionMethodOptions = [
    { label: t('AccountSelectionMethod.Options.manual'), value: 'manual' },
    { label: t('AccountSelectionMethod.Options.online'), value: 'online' },
  ];

  type PaymentSelectionMethodOption = {
    label: string;
    value: string;
    featureKey: string;
    visible: boolean;
    renderLabel?: JSX.Element;
  };

  const PaymentSelectionMethodOptions: PaymentSelectionMethodOption[] = [
    {
      label: t('RequestPaymentMethodType.Options.bankAccount'),
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      label: t('RequestPaymentMethodType.Options.interacETransfer'),
      renderLabel: <InteracWordmark type={formType === 'Pay' ? 'eTransfer' : 'moneyRequest'} />,
      value: 'email',
      featureKey: formType === 'Pay' ? 'Interac e-Transfer' : 'Interac e-Transfer Money Request',
      visible: country === 'CA' && formType !== 'Flex' && formType !== 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.email'),
      value: 'email',
      featureKey: 'Email',
      visible: country === 'CA' && formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.creditCard'),
      value: 'credit',
      featureKey: 'Credit Card',
      visible: formType !== 'Pay',
    },
    {
      label: t('RequestPaymentMethodType.Options.debitCard'),
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.googlePay'),
      value: 'googlePay',
      featureKey: 'Google Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      label: t('RequestPaymentMethodType.Options.paypal'),
      value: 'paypal',
      featureKey: 'PayPal',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.venmo'),
      value: 'venmo',
      featureKey: 'Venmo',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
  ];

  type PaymentMethodOption = { label: string; value: string; isComingSoon: boolean };
  const paymentMethodsOptions: PaymentMethodOption[] = [
    {
      label: t('PaymentMethodOptions.Options.new', { contactName }),
      value: 'New',
      isComingSoon: false,
    },
    {
      label: t('PaymentMethodOptions.Options.existing', { contactName }),
      value: 'Existing',
      isComingSoon: true,
    },
  ];

  type PaymentMethodMultiOption = { label: string; value: string; disabled: boolean };

  // type VerificationMethodOption = {
  //   label: string;
  //   value: string;
  //   featureKey: string;
  //   disabled: boolean;
  //   tooltip?: React.ReactNode;
  // };

  return [
    {
      label: () => t('PaymentMethodOptions.label'),
      fieldName: 'PaymentMethodDisplayOptions',
      getInputState: () => (contactName === '' ? 'disabled' : 'required'),
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => values.PaymentMethodDisplayOptions,
      tooltip: () => (
        <Tooltip
          information={
            <Box gapSize="14px" flx="col">
              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>{t('PaymentMethodOptions.Tooltip.newPaymentMethodTitle')}</h3>
                <div>{t('PaymentMethodOptions.Tooltip.newPaymentMethodDescription')}</div>
              </Box>
              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>
                  {t('PaymentMethodOptions.Tooltip.existingPaymentMethodTitle')}
                </h3>
                <div>{t('PaymentMethodOptions.Tooltip.existingPaymentMethodDescription')}</div>
              </Box>
            </Box>
          }
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData, ctx }) => {
        const selectFieldData = getFormikData<string>({});
        const multiSelectFieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });
        const filteredPaymentMethods = paymentMethods.filter(
          (paymentMethod) =>
            ctx.values.PaymentSelectionMethod?.includes(mapToPaymentSelectionValue(paymentMethod.PaymentMethod)) ||
            !ctx.values.PaymentSelectionMethod ||
            ctx.values.PaymentSelectionMethod === '',
        );
        const paymentMethodsMuiltiOptions: PaymentMethodMultiOption[] = [
          {
            label: t('PaymentMethodOptions.Options.new', { contactName }),
            value: 'New',
            disabled: false,
          },
          {
            label: t('PaymentMethodOptions.Options.existing', { contactName }),
            value: 'Existing',
            disabled: filteredPaymentMethods.length === 0,
          },
        ];

        return formType === 'Flex' ? (
          <Select
            version="2"
            placeholder={t('PaymentMethodOptions.placeholder')}
            options={paymentMethodsOptions}
            sort={false}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault }) => (
              <Option
                onClick={() => {
                  if (o.isComingSoon) {
                    preventDefault();
                  }
                }}
              >
                <Box flx="row" ai="center" jc="sb" width="100%">
                  <div className={classes(o.isComingSoon && 'optionDisabled')}> {o.label}</div>
                  {o.isComingSoon && <Chip label={t('PaymentMethodOptions.comingSoonChip')} />}

                  {selected && <SelectedIcon />}
                </Box>
              </Option>
            )}
            renderDropdownHeader={() => (
              <div className={classes('selectHeader')}>{t('PaymentMethodOptions.selectHeader')}</div>
            )}
            {...selectFieldData}
          />
        ) : (
          <MultiSelect
            placeholder={t('PaymentMethodOptions.placeholder')}
            disableOptionWhen={(option) => option.disabled}
            options={paymentMethodsMuiltiOptions}
            {...multiSelectFieldData}
          />
        );
      },
      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('ReceiverPhoneNumber.label'),
      fieldName: 'ReceiverPhoneNumber',
      getInputState: (ctx) => (ctx.values.PaymentMethodDisplayOptions === 'New' ? 'optional' : 'required'),
      showField: (values: ElinxInfo) =>
        !!values.PaymentMethodDisplayOptions && values.PaymentMethodDisplayOptions !== 'New',
      serializeField: (values: ElinxInfo) => values.ReceiverPhoneNumber,
      tooltip: () => (
        <Tooltip information={<div>{t('ReceiverPhoneNumber.tooltip')}</div>} position="top" width="298px" />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.PaymentMethodDisplayOptions !== 'New' ? Yup.mixed().required() : Yup.mixed()),
    },
    {
      label: () => t('RequestPaymentMethodType.label'),
      fieldName: 'PaymentSelectionMethod',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) =>
        values.PaymentSelectionMethod?.split(',')
          .filter((paymentMethod) => paymentMethod)
          .join(','),
      tooltip: () => (
        <Tooltip
          information={
            <Box gapSize="4px" flx="col">
              <h3 className={classes('toolTipHeader')}>{t('RequestPaymentMethodType.Tooltip.title')}</h3>
              <div>{t('RequestPaymentMethodType.Tooltip.description')}</div>
            </Box>
          }
          position="top"
          width="394px"
        />
      ),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });
        return (
          <MultiSelect
            placeholder={t('RequestPaymentMethodType.placeholder')}
            options={
              isToday(ctx.values.ScheduleStartDate) && ctx.values.Frequency === 'single'
                ? PaymentSelectionMethodOptions.filter((option) => option.visible)
                : PaymentSelectionMethodOptions.filter((option) => option.visible && option.value === 'bank')
            }
            renderOption={(option, args) => (
              <SelectOptionLocked {...args} featureKey={option.featureKey}>
                {option.renderLabel || option.label}
              </SelectOptionLocked>
            )}
            sort={false}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
    // TODO: Uncomment when verification is needed
    // {
    //   label: () => t('Verify.label'),
    //   fieldName: 'Verify',
    //   getInputState: () => 'optional',
    //   showField: () => true,
    //   serializeField: (values: ElinxInfo) =>
    //     values.Verify?.split(',')
    //       .filter((verificationMethod) => verificationMethod)
    //       .join(','),

    //   renderField: ({ ctx, getFormikData }) => {
    //     const fieldData = getFormikData<string[], string>({
    //       formatValue: (value) => (value ? value.split(',') : []),
    //     });
    //     const PaymentVerificationMethodOptions: VerificationMethodOption[] = [
    //       {
    //         label: t('Verify.Options.bank'),
    //         value: 'bank',
    //         featureKey: 'Bank Account Verification',
    //         disabled:
    //           !ctx.values.PaymentSelectionMethod?.includes('bank') || ctx.values.AccountSelectionMethod === 'online',
    //       },
    //       {
    //         label: t('Verify.Options.interac'),
    //         value: 'interac',
    //         featureKey: 'Interac Verification',
    //         disabled: !ctx.values.PaymentSelectionMethod?.includes('email'),
    //       },
    //     ];
    //     return (
    //       <MultiSelect
    //         placeholder={t('Verify.placeholder')}
    //         options={PaymentVerificationMethodOptions}
    //         renderOption={(option, args) => (
    //           <SelectOptionLocked {...args} featureKey={option.featureKey} disabled={option.disabled}>
    //             {option.label}
    //           </SelectOptionLocked>
    //         )}
    //         sort={false}
    //         {...fieldData}
    //       />
    //     );
    //   },

    //   validate: () => Yup.mixed().notRequired(),
    // },
    {
      label: () => t('AccountSelectionMethod.label'),
      fieldName: 'AccountSelectionMethod',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank') && formType === 'Connect', // only show when bank account has been selected
      serializeField: (values: ElinxInfo) => {
        const accountValues = values.AccountSelectionMethod?.split(',');
        if (accountValues && accountValues.length > 1) {
          return 'any';
        }
        if (accountValues && accountValues.length === 1) {
          return accountValues[0];
        }
        return '';
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });
        return (
          <MultiSelect
            placeholder={t('AccountSelectionMethod.placeholder')}
            options={AccountSelectionMethodOptions}
            {...fieldData}
            // TODO: Uncomment when verification is needed
            // onChange={(e) => {

            //   if (e.target.value === 'online' && ctx.values.Verify?.includes('bank')) {
            //     const updatedVerify = ctx.values.Verify.split(',')
            //       .map((v) => v.trim())
            //       .filter((v) => v !== 'bank')
            //       .join(',');

            //     ctx.setFieldValue('Verify', updatedVerify);
            //   }
            //   fieldData.onChange(e);
            // }}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
    // move to the first container for now
    // {
    //   label: () => 'Email Address',
    //   fieldName: 'OverrideEmailAddress',
    //   getInputState: (ctx) => (ctx.values.SendEmail ? 'required' : 'optional'),
    //   showField: (values) => !!values.SendEmail && values.Contact !== 'new',
    //   serializeField: (values: ElinxInfo) => values.OverrideEmailAddress,
    //   renderField: ({ getFormikData }) => {
    //     const fieldData = getFormikData<string>({});

    //     return <TextField variant="outlineShadow" disabled={isReview} placeholder="Enter your name" {...fieldData} />;
    //   },
    //   validate: (ctx) => (ctx.values.SendEmail ? Yup.mixed().required('This value is required.') : Yup.mixed()),
    // },
  ];
}
export function useBankAccountRows(formType: ElinxType, country: string): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.BankAccount' });
  const lockedFeatures = useLockedFeatures();

  type BankingPaymentRailOption = {
    label: React.ReactNode;
    value: string;
    isLocked: boolean;
    visible: boolean;
    isRecurringEnabled: boolean;
  };

  const BankingPaymentRailOptions: BankingPaymentRailOption[] = [
    {
      label: t('BankingPaymentRail.Options.eft'),
      value: 'eft',
      isLocked: !lockedFeatures.EFTSendEnabled && !lockedFeatures.EFTSendEnabled,
      visible: country === 'CA',
      isRecurringEnabled: true,
    },
    {
      label: 'VoPay Instant',
      value: 'vopayinstant',
      isLocked: !lockedFeatures.RTRWithdrawEnabled,
      visible: country === 'CA' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      label: t('BankingPaymentRail.Options.ach'),
      value: 'ach',
      isLocked: !lockedFeatures.ACHFundEnabled && !lockedFeatures.ACHWithdrawEnabled,
      visible: country === 'US',
      isRecurringEnabled: true,
    },
    {
      label: t('BankingPaymentRail.Options.rtp'),
      value: 'rtp',
      isLocked: !lockedFeatures.RTPWithdrawEnabled,
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      label: t('BankingPaymentRail.Options.fedwire'),
      value: 'fedwire',
      isLocked: !lockedFeatures.FedwireWithdrawEnabled,
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
  ];

  const AccountSelectionMethodOptions = [
    { label: t('AccountSelectionMethod.Options.manual'), value: 'manual' },
    { label: t('AccountSelectionMethod.Options.online'), value: 'online' },
  ];

  return [
    {
      label: () => t('BankingPaymentRail.label'),
      fieldName: 'BankingPaymentRail',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank'),
      serializeField: (values: ElinxInfo) => values.BankingPaymentRail,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('BankingPaymentRail.placeholder')}
            options={BankingPaymentRailOptions.filter(
              (option) => option.visible && (ctx.values.Frequency !== 'recurring' || option.isRecurringEnabled),
            )}
            sort={false}
            renderOption={(o, { Option, preventDefault }) => (
              <Option
                onClick={() => {
                  if (o.isLocked) {
                    preventDefault();
                  }
                }}
              >
                <Box flx="row" ai="center" jc="sb" width="100%">
                  <div className={classes(o.isLocked && 'optionDisabled')}> {o.label}</div>
                  {o.isLocked && (
                    <Icon width="min-content" height="min-content" icon="lock2" size="sm" color="medium-light" />
                  )}
                </Box>
              </Option>
            )}
            {...fieldData}
          />
        );
      },
      validate: (ctx) =>
        ctx.values.PaymentSelectionMethod?.includes('bank') && formType !== 'Connect'
          ? Yup.mixed().required()
          : Yup.mixed(),
    },
    {
      label: () => t('AccountSelectionMethod.label'),
      fieldName: 'AccountSelectionMethod',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank'),
      serializeField: (values: ElinxInfo) => {
        const accountValues = values.AccountSelectionMethod?.split(',');
        if (accountValues && accountValues.length > 1) {
          return 'any';
        }
        if (accountValues && accountValues.length === 1) {
          return accountValues[0];
        }
        return '';
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });
        return (
          <MultiSelect
            placeholder={t('AccountSelectionMethod.placeholder')}
            options={AccountSelectionMethodOptions}
            {...fieldData}
            // TODO: Uncomment when verification is needed
            // onChange={(e) => {

            //   if (e.target.value === 'online' && ctx.values.Verify?.includes('bank')) {
            //     const updatedVerify = ctx.values.Verify.split(',')
            //       .map((v) => v.trim())
            //       .filter((v) => v !== 'bank')
            //       .join(',');

            //     ctx.setFieldValue('Verify', updatedVerify);
            //   }
            //   fieldData.onChange(e);
            // }}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
  ];
}

export function useElinxPartialPaymentRows(formType: ElinxType, isEnabled: boolean): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.PartialPayment' });
  const now = format(new Date(), 'yyyy-MM-dd');
  return [
    {
      label: () => t('MinimumPaymentAccepted.label'),
      fieldName: 'MinimumPaymentAccepted',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial'),
      serializeField: (values: ElinxInfo) =>
        formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial') && values.MinimumPaymentAccepted
          ? vp.sanitizeToNumeric(String(values.MinimumPaymentAccepted))
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            placeholder={t('MinimumPaymentAccepted.placeholder')}
            {...fieldData}
          />
        );
      },
      tooltip: () => <Tooltip information={t('MinimumPaymentAccepted.tooltip')} position="top" width="394px" />,
      validate: (ctx) =>
        Yup.string()
          .test('amount-validation', t('MinimumPaymentAccepted.Validation.greaterThanZero'), (value) => {
            const amountValidation = Number(vp.sanitizeToNumeric(value)) <= 0.0;
            return !amountValidation;
          })
          .test(
            'amount-validation',
            t('MinimumPaymentAccepted.Validation.lessThanAmount', { amount: ctx.values.Amount }),
            (value) => {
              const amountValidation =
                Number(vp.sanitizeToNumeric(value)) >= Number(vp.sanitizeToNumeric(ctx.values.Amount));
              return !amountValidation;
            },
          )
          .required(),
    },
    {
      label: () => <span style={{ opacity: 0 }}>{t('ScheduleRemainingAmount.label')}</span>,
      fieldName: 'ScheduleRemainingAmount',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial'),
      serializeField: (values: ElinxInfo) =>
        formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial')
          ? values.ScheduleRemainingAmount
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});
        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={
              <Box gapSize="4px">
                {t('ScheduleRemainingAmount.label')}{' '}
                <Tooltip position="top" information={t('ScheduleRemainingAmount.tooltip')} />
              </Box>
            }
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('RemainderPaymentDate.label'),
      fieldName: 'RemainderPaymentDate',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => {
        return (
          !!values.ScheduleRemainingAmount &&
          isEnabled &&
          formType === 'Flex' &&
          !!values.FlexiblePaymentOptions?.includes('partial')
        );
      },
      serializeField: (values: ElinxInfo) => {
        if (!values.ScheduleRemainingAmount) {
          return undefined;
        }
        return formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial')
          ? format(new Date(Number(values.RemainderPaymentDate)), 'yyyy-MM-dd')
          : undefined;
      },
      tooltip: () => <Tooltip information={t('RemainderPaymentDate.tooltip')} position="top" width="394px" />,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const selectedDate = new Date(Number(e.target.value));
            const endDate = new Date(Number(ctx.values.ScheduleEndDate));
            if (isBefore(selectedDate, endDate)) {
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleEndDate', newDate.toString());
            }
          },
        });
        return (
          <DatepickerSimple
            variant="outlineShadow"
            min={addDays(new Date(), 1).getTime() as vp.Timestamp}
            iconPosition="left"
            {...fieldData}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string()
          .required()
          .test(
            'start-date-validation',
            t('RemainderPaymentDate.invalidDateValidation', { now }),
            (value: string | undefined) => {
              if (!value) return false;
              const selectedDate = new Date(Number(value));
              return !isBefore(selectedDate, addDays(new Date(), 1));
            },
          ),
    },
  ];
}

export function useElinxInstallmentPlansRows(formType: ElinxType, isEnabled: boolean): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.InstallmentPlan' });

  const installmentFrequencyOptions: vp.SelectOption<BasicOptions>[] = [
    { label: t('InstallmentFrequency.Options.weekly'), value: 'weekly' },
    { label: t('InstallmentFrequency.Options.biweekly'), value: 'biweekly' },
    { label: t('InstallmentFrequency.Options.monthly'), value: 'monthly' },
  ];
  return [
    {
      label: () => t('NumberOfInstallments.label'),
      fieldName: 'NumberOfInstallments',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments'),
      serializeField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments')
          ? vp.sanitizeToNumeric(values.NumberOfInstallments)
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="numericWholePositive"
            placeholder={t('NumberOfInstallments.placeholder')}
            {...fieldData}
          />
        );
      },
      tooltip: () => {
        return <Tooltip information={t('NumberOfInstallments.tooltip')} position="top" width="394px" />;
      },
      validate: () =>
        Yup.number()
          .transform((value) => Number(value) || 0)
          .min(1, t('NumberOfInstallments.atLeastOneValidation'))
          .required(),
    },
    {
      label: () => t('InstallmentFrequency.label'),
      fieldName: 'InstallmentFrequency',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments'),
      getInputState: () => 'required',
      serializeField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments')
          ? values.InstallmentFrequency
          : undefined,
      tooltip: () => {
        return <Tooltip information={t('InstallmentFrequency.tooltip')} position="top" width="394px" />;
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('InstallmentFrequency.placeholder')}
            options={installmentFrequencyOptions}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
  ];
}

export function useElinxDetailsRows(formType: ElinxType): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ELinxDetails' });
  return [
    {
      label: () => t('SenderName.label'),
      fieldName: 'SenderName',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SenderName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('SenderName.placeholder')} {...fieldData} />;
      },
      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('RecipientMessage.label'),
      fieldName: 'RecipientMessage',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.RecipientMessage,
      tooltip: () => <Tooltip information={t('RecipientMessage.tooltip')} position="top" width="394px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('RecipientMessage.placeholder')} {...fieldData} />;
      },
    },
    {
      label: () => t('InvoiceNumber.label'),
      fieldName: 'InvoiceNumber',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.InvoiceNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
    {
      label: () => t('UploadInvoice.label'),
      fieldName: 'InvoiceFile',
      getInputState: () => 'optional',
      showField: () => formType !== 'Connect',
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <UploadFile
            id={fieldData.name}
            onChangeFileName={(e: React.ChangeEvent<HTMLInputElement>) =>
              ctx.setFieldValue('InvoiceName', e.target.value)
            }
            valueFileName={ctx.values.InvoiceName}
            maxMBSize={4}
            validExtensions={['jpeg', 'png', 'pdf']}
            base64
            {...fieldData}
          />
        );
      },
    },
  ];
}

export function useElinxSettingsRows(
  isReview: boolean,
  defaultNotificationMethod: vp.ElinxNotificationMethod,
): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ELinxSettings' });

  return [
    {
      label: () => t('SendNotification.label'),
      fieldName: 'SendNotification',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SendNotification,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});
        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={t('SendNotification.placeholder')}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('SendReminder.label'),
      fieldName: 'SendReminder',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SendReminder,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});
        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={t('SendReminder.checkboxLabel')}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('ReminderFrequency.label'),
      fieldName: 'ReminderFrequency',
      getInputState: (ctx) => (ctx.values.SendReminder ? 'required' : 'disabled'),
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.ReminderFrequency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.ReminderFrequency ? Yup.mixed().required() : Yup.mixed()),
    },
    {
      label: () => t('NotificationMethod.label'),
      fieldName: 'NotificationMethod',
      getInputState: (ctx) => (ctx.values.SendNotification ? 'optional' : 'disabled'),
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.NotificationMethod,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const options = {
          email: t('NotificationMethod.Options.email'),
          sms: t('NotificationMethod.Options.sms'),
          ...(defaultNotificationMethod === 'whatsapp' && { whatsapp: t('NotificationMethod.Options.whatsapp') }),
        };
        return (
          <Radio varient="outlineShadow" readOnly={isReview || fieldData.disabled} options={options} {...fieldData} />
        );
      },
    },
    {
      label: () => t('ReceiverPhoneNumber.label'),
      fieldName: 'ReceiverPhoneNumber',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        !values.PaymentMethodDisplayOptions?.includes('Existing') && values.NotificationMethod === 'sms',
      serializeField: (values: ElinxInfo) => values.ReceiverPhoneNumber,
      tooltip: () => <Tooltip information={t('ReceiverPhoneNumber.tooltip')} position="top" width="298px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required(),
    },
  ];
}

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());
  return isEqual(date, today);
}
export const mapToPaymentSelectionValue = (paymentMethod: vp.PaymentMethodType): string => {
  const mapping: Record<vp.PaymentMethodType, string> = {
    BankAccount: 'bank',
    CreditCard: 'credit',
    DebitCard: 'debitcard',
    PhoneNumber: 'email',
    Email: 'email',
    GooglePay: 'googlepay',
    ApplePay: 'applepay',
    PayPal: 'paypal',
    Venmo: 'venmo',
  };

  return mapping[paymentMethod] || '';
};
